/**
 * useAuth — the 3-tier auth cascade for the v2 ora Outlook add-in.
 *
 * Plan §"Auth rewiring (3-tier layered…)". Exposes the same surface as
 * v1's `useAuth()` so every ported v1 component works unchanged:
 *
 *   { isAuthenticated, signOut, getAccessToken, userId, displayName,
 *     email, signIn }
 *
 * Tier 1 — Office.auth.getAccessToken({ allowSignInPrompt: false })
 *           on success → exchangeForProductApiToken (Product API JWT).
 *           Errors 13000 / 13002 / 13007 fall through silently.
 *
 * Tier 2 — Office Dialog → Product API handoff (services/authClient.ts).
 *           Default path on Highstreet today. On dialog-unavailable /
 *           handoff-timeout, fall through to Tier 3.
 *
 * Tier 3 — MSAL acquireTokenSilent → acquireTokenPopup on
 *           InteractionRequiredAuthError. Uses acquireTokenRedirect when
 *           outside Outlook (plain browser dev where popup is fine but
 *           the cleaner UX outside the WebView2 sandbox is redirect).
 *
 * Each token acquisition logs `auth_tier` so support can immediately
 * see which path won the most recent acquisition. The shim also lifts
 * profile claims (userId / email / displayName) into a singleton-ish
 * subscribable state so components can reactively render the current
 * user without re-running the cascade.
 */

import { useCallback, useEffect, useState } from "react";
import {
  getProductApiToken,
  signOut as authClientSignOut,
  AuthError,
  DialogCancelledError,
  DialogAlreadyOpenError,
  MissingOfficeError,
} from "../services/authClient";
import {
  exchangeForProductApiToken,
  SsoExchangeError,
} from "./exchangeForProductApiToken";
import { ensureMsalInstance } from "./AuthProvider";
import { apiRequest, loginRequest } from "./msalConfig";
import { logger, type AuthTier } from "../obs/logger";

// -----------------------------------------------------------------------------
// Office.js error codes documented at
// https://learn.microsoft.com/javascript/api/office/office.error
// -----------------------------------------------------------------------------
const OFFICE_SSO_FALLTHROUGH_CODES = new Set([
  13000, // Add-in does not support the user's identity
  13002, // The user aborted the consent prompt (or no consent given)
  13007, // The application is unsupported
]);

function isExpectedOfficeSsoFailure(err: unknown): boolean {
  if (!err) return false;
  // Office.AsyncResultStatus.Failed wraps the original code on `.code`.
  const code = (err as { code?: number; error?: { code?: number } })?.code
    ?? (err as { error?: { code?: number } })?.error?.code;
  if (typeof code === "number" && OFFICE_SSO_FALLTHROUGH_CODES.has(code)) {
    return true;
  }
  // Some hosts surface the code in the message string.
  const msg = (err as { message?: string })?.message ?? "";
  return /\b(13000|13002|13007)\b/.test(msg);
}

function isTier2DialogUnavailable(err: unknown): boolean {
  if (!err) return false;
  if (err instanceof MissingOfficeError) return true;
  if (err instanceof DialogCancelledError) return true;
  if (err instanceof DialogAlreadyOpenError) return true;
  if (err instanceof AuthError) {
    const msg = err.message ?? "";
    if (/timed out/i.test(msg)) return true;
    if (/handoff/i.test(msg) && /failed/i.test(msg)) return true;
    if (/dialog/i.test(msg) && /unavailable/i.test(msg)) return true;
  }
  return false;
}

// -----------------------------------------------------------------------------
// Tier 1 — Office.auth.getAccessToken silent SSO.
// -----------------------------------------------------------------------------

interface OfficeAuth {
  getAccessToken(options: {
    allowSignInPrompt?: boolean;
    allowConsentPrompt?: boolean;
    forMSGraphAccess?: boolean;
  }): Promise<string>;
}

async function tier1OfficeSso(): Promise<{ token: string; tier: AuthTier } | null> {
  const Office = (globalThis as unknown as { Office?: { auth?: OfficeAuth } }).Office;
  if (!Office?.auth?.getAccessToken) return null;
  const ssoToken = await Office.auth.getAccessToken({ allowSignInPrompt: false });
  try {
    const productApiToken = await exchangeForProductApiToken(ssoToken);
    return { token: productApiToken, tier: "office_sso" };
  } catch (err) {
    // The server returns 501 when the Tier-1 exchange handler is still a stub
    // (non-Development environments). Treat this as a graceful fall-through to
    // Tier 2 rather than a hard failure — the same outcome as error 13000, just
    // from the server side instead of the Office runtime.
    // TODO: remove this 501 guard once ExchangeSsoTokenHandler is replaced with
    // real JWKS validation and the server-side env guard is lifted.
    if (err instanceof SsoExchangeError && err.status === 501) {
      logger.info("Auth cascade: Tier 1 exchange stub active, falling through to Tier 2", {
        auth_tier: "office_sso",
      });
      return null;
    }
    throw err;
  }
}

// -----------------------------------------------------------------------------
// Tier 2 — Office Dialog → Product API handoff (services/authClient.ts).
// -----------------------------------------------------------------------------

async function tier2DialogHandoff(): Promise<{ token: string; tier: AuthTier }> {
  const token = await getProductApiToken();
  return { token, tier: "dialog_handoff" };
}

// -----------------------------------------------------------------------------
// Tier 3 — MSAL silent → popup / redirect.
// -----------------------------------------------------------------------------

interface AccountClaims {
  userId: string;
  displayName: string;
  email: string;
}

function isInsideOutlook(): boolean {
  // typeof Office is non-null in any page that loaded office.js, but
  // Office.context.host is null outside of an Office host. WebView2
  // blocks redirect, so popup is the right interactive path inside
  // Outlook.
  const Office = (globalThis as unknown as { Office?: { context?: { host?: unknown } } })
    .Office;
  return Office?.context?.host != null;
}

async function tier3Msal(): Promise<{
  token: string;
  tier: AuthTier;
  claims: AccountClaims;
}> {
  const instance = await ensureMsalInstance();
  let accounts = instance.getAllAccounts();

  // First-time login if no cached account.
  if (accounts.length === 0) {
    if (isInsideOutlook()) {
      const result = await instance.loginPopup(loginRequest);
      if (result.account) instance.setActiveAccount(result.account);
    } else {
      // Outside Outlook (plain browser dev) — redirect is the cleaner
      // UX. Note: redirect doesn't return a token in the same call;
      // we throw so the caller can re-enter the cascade after the
      // browser navigation round-trip.
      await instance.loginRedirect(loginRequest);
      throw new Error("Tier 3 redirect dispatched — token will resolve after redirect");
    }
    accounts = instance.getAllAccounts();
  }
  const account = accounts[0];
  if (!account) {
    throw new Error("Tier 3: no account after login");
  }

  // Acquire token silent first.
  let accessToken: string;
  let usedTier: AuthTier;
  const InteractionRequiredAuthError =
    (await import("@azure/msal-browser")).InteractionRequiredAuthError;
  try {
    const silent = await instance.acquireTokenSilent({ ...apiRequest, account });
    accessToken = silent.accessToken;
    usedTier = "msal_silent";
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      if (isInsideOutlook()) {
        const popup = await instance.acquireTokenPopup(apiRequest);
        accessToken = popup.accessToken;
        usedTier = "msal_popup";
      } else {
        await instance.acquireTokenRedirect(apiRequest);
        throw new Error("Tier 3 redirect dispatched — token will resolve after redirect");
      }
    } else {
      throw err;
    }
  }

  const claims: AccountClaims = {
    userId: account.localAccountId,
    displayName: account.name ?? "",
    email: account.username,
  };
  return { token: accessToken, tier: usedTier, claims };
}

// -----------------------------------------------------------------------------
// Cascade orchestrator
// -----------------------------------------------------------------------------

export type SignInVia = "default" | "tier2" | "msal";

interface AuthState {
  isAuthenticated: boolean;
  userId: string;
  displayName: string;
  email: string;
  /** Which tier produced the most recently issued token. */
  authTier: AuthTier | null;
}

const INITIAL_STATE: AuthState = {
  isAuthenticated: false,
  userId: "",
  displayName: "",
  email: "",
  authTier: null,
};

let _state: AuthState = INITIAL_STATE;
const _subscribers = new Set<(s: AuthState) => void>();

function publish(patch: Partial<AuthState>): void {
  _state = { ..._state, ...patch };
  _subscribers.forEach((fn) => fn(_state));
}

function subscribe(fn: (s: AuthState) => void): () => void {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

/**
 * A dev/test host that signs the pane in by other means (the capture harness
 * under src/proof, which skips MSAL and Office SSO) publishes its user here,
 * so the shell renders signed in. Never called by the shipped add-in.
 */
export function publishSignedInUser(user: {
  userId: string;
  displayName: string;
  email: string;
}): void {
  publish({ isAuthenticated: true, authTier: "stub", ...user });
}

/** Test helper — reset the singleton between cases. */
export function _resetUseAuthForTests(): void {
  _state = INITIAL_STATE;
  _subscribers.clear();
}

/**
 * Run the 3-tier cascade. Returns a Product-API-scoped JWT and the
 * tier that produced it. Throws AuthError / Tier-3 errors only if
 * every tier fails or returns an unexpected error.
 */
async function cascade(): Promise<{ token: string; tier: AuthTier; claims?: AccountClaims }> {
  // ---- Tier 1 ----
  try {
    const t1 = await tier1OfficeSso();
    if (t1) {
      logger.info("Auth cascade: Tier 1 succeeded", { auth_tier: t1.tier });
      return t1;
    }
    // Office.auth not present — fall through silently.
  } catch (err) {
    if (!isExpectedOfficeSsoFailure(err)) {
      logger.warn("Auth cascade: Tier 1 raised unexpected error", {
        auth_tier: "office_sso",
        error: String(err),
      });
      throw err;
    }
    logger.info("Auth cascade: Tier 1 expected fall-through", {
      auth_tier: "office_sso",
      error: String(err),
    });
  }

  // ---- Tier 2 ----
  try {
    const t2 = await tier2DialogHandoff();
    logger.info("Auth cascade: Tier 2 succeeded", { auth_tier: t2.tier });
    return t2;
  } catch (err) {
    if (!isTier2DialogUnavailable(err)) {
      logger.warn("Auth cascade: Tier 2 raised unexpected error", {
        auth_tier: "dialog_handoff",
        error: String(err),
      });
      throw err;
    }
    logger.info("Auth cascade: Tier 2 expected fall-through", {
      auth_tier: "dialog_handoff",
      error: String(err),
      ...(err instanceof DialogAlreadyOpenError && { auth_multi_pane_fallthrough: true }),
    });
  }

  // ---- Tier 3 ----
  const t3 = await tier3Msal();
  logger.info("Auth cascade: Tier 3 succeeded", { auth_tier: t3.tier });
  return t3;
}

/**
 * Cached synchronous-ish access token. We cache the most recent token
 * keyed only by which tier produced it; the underlying tier
 * implementations have their own expiry logic (Tier 2's authClient is
 * the canonical cache; Tier 3's MSAL instance handles its own).
 */
async function acquireToken(): Promise<string> {
  const result = await cascade();
  publish({
    isAuthenticated: true,
    authTier: result.tier,
    userId: result.claims?.userId ?? _state.userId,
    displayName: result.claims?.displayName ?? _state.displayName,
    email: result.claims?.email ?? _state.email,
  });
  return result.token;
}

/**
 * Sign in entry point. Default behaviour: kick off the cascade so the
 * user gets the fastest path that works on their host. `via: "msal"`
 * forces Tier 3 directly for diagnostics or admin-consent flows.
 */
async function performSignIn(via: SignInVia): Promise<void> {
  if (via === "msal") {
    const t3 = await tier3Msal();
    publish({
      isAuthenticated: true,
      authTier: t3.tier,
      userId: t3.claims.userId,
      displayName: t3.claims.displayName,
      email: t3.claims.email,
    });
    return;
  }
  await acquireToken();
}

/**
 * Sign out — clear Tier 2's localStorage cache AND MSAL cache so
 * neither tier holds a stale token. We deliberately do NOT clear the
 * Office.auth cache because Office controls that and there's no API
 * to invalidate it from the add-in side.
 */
async function performSignOut(): Promise<void> {
  authClientSignOut();
  // Best effort: clear MSAL's cache only if the instance has actually
  // been initialised. We do NOT instantiate it here — the whole point
  // of lazy MSAL is that signOut on a Tier 2 happy path doesn't pay
  // the MSAL cold-start cost.
  try {
    const { peekMsalInstance } = await import("./AuthProvider");
    const instance = peekMsalInstance();
    if (instance) {
      const accounts = instance.getAllAccounts();
      for (const acct of accounts) {
        try {
          await instance.clearCache({ account: acct });
        } catch {
          // ignore — clearCache only exists on msal-browser >= 3.x
        }
      }
    }
  } catch {
    // ignore — never block sign-out on cleanup failures
  }
  publish(INITIAL_STATE);
  logger.info("Auth cascade: signed out", {});
}

// -----------------------------------------------------------------------------
// Public hook
// -----------------------------------------------------------------------------

export interface UseAuthValue {
  isAuthenticated: boolean;
  userId: string;
  displayName: string;
  email: string;
  /** Which tier produced the most recently issued token (null if none). */
  authTier: AuthTier | null;
  /** Run the cascade; returns a Product-API-scoped JWT. */
  getAccessToken: () => Promise<string>;
  /**
   * `via: "msal"` forces Tier 3 directly. Defaults to running the full
   * cascade.
   */
  signIn: (opts?: { via?: SignInVia }) => Promise<void>;
  signOut: () => Promise<void>;
}

export function useAuth(): UseAuthValue {
  const [state, setState] = useState<AuthState>(_state);

  useEffect(() => {
    return subscribe((s) => setState(s));
  }, []);

  const getAccessToken = useCallback(() => acquireToken(), []);

  const signIn = useCallback((opts?: { via?: SignInVia }) => {
    return performSignIn(opts?.via ?? "default");
  }, []);

  const signOut = useCallback(() => performSignOut(), []);

  return {
    isAuthenticated: state.isAuthenticated,
    userId: state.userId,
    displayName: state.displayName,
    email: state.email,
    authTier: state.authTier,
    getAccessToken,
    signIn,
    signOut,
  };
}

// -----------------------------------------------------------------------------
// Test-only direct accessors (so tests can assert the cascade without
// rendering the full hook surface).
// -----------------------------------------------------------------------------

export const _testInternals = {
  cascade,
  tier1OfficeSso,
  tier2DialogHandoff,
  tier3Msal,
  isExpectedOfficeSsoFailure,
  isTier2DialogUnavailable,
};
